import { Box, Button, Stack, TextField } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogPlanLlmPrompt } from '../../api/DialogPlanLlmPromptApi';
import { DialogPromptConversationReadWithBlankSpeak, IDialogPromptSentence, SPEAKER_NAME_A, SPEAKER_NAME_B } from '../../api/DialogPromptApi';
import { IDialogPromptConversation } from '../../api/DialogPromptConversationApi';
import AppBarContext from '../../context/AppBarContext';
import { useConversationPinyin } from '../../hook/useConversationPinyin';

interface DialogPlanLlmPromptConversationReadWithBlankSpeakGeneratedProps {
    dialogPrompt?: DialogPromptConversationReadWithBlankSpeak; // if llmPrompt was generated
    dialogPromptConversation?: IDialogPromptConversation | null; // if llmPrompt was generated
    onSubmit?: (updatedDialogPlanLlmPrompt: DialogPlanLlmPrompt, setIsSaving: React.Dispatch<React.SetStateAction<boolean>>) => void;
    onDelete?: (dialogPlanLlmPromptId: number) => void;

    isRegenerating?: boolean;
    handleLlmPromptChange?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleRegenerateDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => void;
}

function isBlankCompatibleWithSource(source: string, blank: string): boolean {
    if (source.length !== blank.length) {
        return false;
    }
    for (let i = 0; i < source.length; i++) {
        if (source[i] !== blank[i] && blank[i] !== "*") {
            return false;
        }
    }
    return true;
}

function normalizeBlankListForConversation(
    conversationSentenceList: IDialogPromptSentence[],
    blankList: string[],
): string[] {
    return conversationSentenceList.map((sentence, index) => {
        const source = sentence.sentence_text ?? '';
        const blank = blankList[index] ?? source;
        return isBlankCompatibleWithSource(source, blank) ? blank : source;
    });
}

function buildBlankPromptSentenceList(
    conversationSentenceList: IDialogPromptSentence[],
    blankList: string[],
    dialogPromptId?: number,
): IDialogPromptSentence[] {
    return conversationSentenceList.map((sentence, index) => ({
        id: 0,
        dialog_prompt_id: dialogPromptId,
        sentence_id: 0,
        sentence_text_alternate: blankList[index] ?? sentence.sentence_text ?? '',
        speaker_name: sentence.speaker_name ?? (index % 2 === 0 ? SPEAKER_NAME_A : SPEAKER_NAME_B),
        order_index: sentence.order_index ?? index,
    }));
}

const DialogPlanLlmPromptConversationReadWithBlankSpeakGenerated: React.FC<DialogPlanLlmPromptConversationReadWithBlankSpeakGeneratedProps> = ({ dialogPrompt, dialogPromptConversation }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    // const [dialogPromptConversationText, setDialogPromptConversationText] = React.useState("");
    const {
        conversationText,
        conversationPinyin,
        conversationPinyinList,
        conversationErrorText,
        setConversationFields,
        clearConversationErrors,
        handleConversationTextChange,
        handleConversationPinyinChange,
    } = useConversationPinyin({ history, location, setErrorMessage });
    const [sentenceBlankList, setSentenceBlankList] = React.useState<string[]>([]);
    const [sentenceBlankErrorList, setSentenceBlankErrorList] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (dialogPrompt) {
            setDialogPromptFields(dialogPrompt);
        }
    }, [dialogPrompt])

    function setDialogPromptFields(dialogPrompt: DialogPromptConversationReadWithBlankSpeak) {
        if (dialogPrompt.dialog_prompt_sentence_list) {
            const textList = dialogPrompt.dialog_prompt_sentence_list.map(sentence => sentence.sentence_text_alternate == undefined
                ? (sentence.sentence_text == undefined ? "" : sentence.sentence_text)
                : sentence.sentence_text_alternate
            );
            setSentenceBlankList(textList);
            setSentenceBlankErrorList(Array.from({ length: textList.length }, () => ''))
        } else {
            setSentenceBlankList([])
            setSentenceBlankErrorList([])
        }
    }
    React.useEffect(() => {
        if (dialogPromptConversation) {
            setConversationFields(dialogPromptConversation.dialog_prompt_sentence_list)
            clearConversationErrors()
            // // setDialogPromptConversationText(dialogPromptConversation.dialog_prompt_sentence_list)
            // setDialogPromptConversationText(dialogPromptConversation.dialog_prompt_sentence_list
            //     ? dialogPromptConversation.dialog_prompt_sentence_list
            //         .map(item => item.sentence_text)
            //         .join("\n") // Convert array to newline-separated string
            //     : ""
            // )
            const generatedBlankList = dialogPrompt?.dialog_prompt_sentence_list?.map(sentence =>
                sentence.sentence_text_alternate ?? sentence.sentence_text ?? ''
            ) ?? [];
            const textList = normalizeBlankListForConversation(
                dialogPromptConversation.dialog_prompt_sentence_list,
                generatedBlankList,
            );
            setSentenceBlankList(textList);
            setSentenceBlankErrorList(Array.from({ length: textList.length }, () => ''))
        }
    }, [clearConversationErrors, dialogPrompt, dialogPromptConversation, setConversationFields])


    // const handleDialogPromptConversationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const newConversationText = e.target.value;
    //     setDialogPromptConversationText(newConversationText);
    //     // Convert newline-separated string to array
    //     const newSentenceBlankList: string[] = newConversationText.split("\n").filter(line => line.trim() !== "");
    //     setSentenceBlankList(newSentenceBlankList)
    //     setSentenceBlankErrorList(Array.from({ length: newSentenceBlankList.length }, () => ''))

    //     if (dialogPromptConversation) {
    //         // Map each line to an IDialogPromptSentence object
    //         dialogPromptConversation.dialog_prompt_sentence_list = newSentenceBlankList.map((studentResponse, index) => ({
    //             id: 0, // Assuming `id` is generated by the backend
    //             sentence_id: 0, // Assuming this is set later or generated by the backend
    //             sentence_text: studentResponse.trim(), // Assign the trimmed response
    //             speaker_name: index % 2 === 0 ? SPEAKER_NAME_A : SPEAKER_NAME_B,
    //             order_index: index + 1, // Incremental order based on line position
    //         }));
    //     }

    // };
    React.useEffect(() => {
        if (dialogPromptConversation) {
            const dialogPromptSentenceList: IDialogPromptSentence[] = conversationText.split("\n").map((response: string, index: number) => {
                const pinyinRaw = conversationPinyinList.length > index ? conversationPinyinList[index] : '';
                const pinyinList = pinyinRaw.trim().split(/\s+/).filter(Boolean);
                return {
                    id: 0,
                    dialog_prompt_id: dialogPrompt ? dialogPrompt.id : undefined,
                    sentence_id: 0,
                    sentence_text: response,
                    sentence_text_pinyin: pinyinList,
                    speaker_name: index % 2 === 0 ? SPEAKER_NAME_A : SPEAKER_NAME_B,
                    order_index: index
                }
            });
            dialogPromptConversation.dialog_prompt_sentence_list = dialogPromptSentenceList
            if (dialogPrompt) {
                setSentenceBlankList((currentBlankList) => {
                    const normalizedBlankList = normalizeBlankListForConversation(dialogPromptSentenceList, currentBlankList);
                    dialogPrompt.dialog_prompt_sentence_list = buildBlankPromptSentenceList(
                        dialogPromptSentenceList,
                        normalizedBlankList,
                        dialogPrompt.id,
                    );
                    setSentenceBlankErrorList(Array.from({ length: normalizedBlankList.length }, () => ''));
                    return normalizedBlankList;
                });
            }
        }
    }, [conversationText, conversationPinyinList, dialogPrompt, dialogPromptConversation])


    // Function to handle changes in text inputs
    const handleSentenceBlankInputChange = (index: number, event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const updatedValues = [...sentenceBlankList];
        updatedValues[index] = event.target.value;
        setSentenceBlankList(updatedValues);
        setSentenceBlankErrorList(Array.from({ length: updatedValues.length }, () => ''))
        if (dialogPrompt) {
            const conversationSentenceList = dialogPromptConversation?.dialog_prompt_sentence_list ?? [];
            if (conversationSentenceList.length > 0) {
                dialogPrompt.dialog_prompt_sentence_list = buildBlankPromptSentenceList(
                    conversationSentenceList,
                    updatedValues,
                    dialogPrompt.id,
                );
            } else if (dialogPrompt.dialog_prompt_sentence_list[index]) {
                dialogPrompt.dialog_prompt_sentence_list[index].sentence_text_alternate = event.target.value;
            }
        }
    };

    const handleSentenceBlankInputValidation = (index: number) => {
        if (dialogPromptConversation) {
            const originalSentence = dialogPromptConversation.dialog_prompt_sentence_list[index].sentence_text;
            const blankSentence = sentenceBlankList[index];

            setSentenceBlankErrorList((prevList) => {
                const updatedList = [...prevList]; // Create a copy of the previous list
                updatedList[index] = ""; // Set the i-th element to blank
                return updatedList; // Return the updated list
            });

            if (originalSentence == undefined) {
                setSentenceBlankErrorList((prevList) => {
                    const updatedList = [...prevList]; // Create a copy of the previous list
                    updatedList[index] = 'Missing sentence';
                    return updatedList; // Return the updated list
                });
            }
            else if (originalSentence.length != blankSentence.length) {
                setSentenceBlankErrorList((prevList) => {
                    const updatedList = [...prevList]; // Create a copy of the previous list
                    updatedList[index] = 'Not same length';
                    return updatedList; // Return the updated list
                });
            } else {
                for (let i = 0; i < originalSentence.length; i++) {
                    if (originalSentence[i] !== blankSentence[i] && blankSentence[i] !== "*") {
                        setSentenceBlankErrorList((prevList) => {
                            const updatedList = [...prevList]; // Create a copy of the previous list
                            updatedList[index] = 'Mismatch character / Not star';
                            return updatedList; // Return the updated list
                        });
                    }
                }
            }
        }
    }


    return (<>
        <Stack spacing={4} sx={{ width: '100%', margin: '0 auto', pt: 2, pb: 4 }}>

            <TextField
                label="Conversation Dialog"
                value={conversationText}
                onChange={handleConversationTextChange}
                multiline
                rows={6}
                inputProps={{
                    style: {
                        resize: 'vertical',
                        overflow: 'hidden',
                    },
                }}
            />
            <TextField
                label="Conversation Pinyin"
                value={conversationPinyin}
                onChange={handleConversationPinyinChange}
                multiline
                rows={6}
                sx={{ width: '100%' }}
                required
                error={conversationErrorText != null}
                helperText={conversationErrorText}
                inputProps={{
                    style: {
                        resize: 'vertical',
                        overflow: 'hidden',
                    },
                }}
            />
            {/* Hidden spacer button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button variant="contained"
                    disabled={true}
                    sx={{ opacity: 0 }}
                >
                    Regenerate
                </Button>
            </Box>
            <div>
                Replace any characters with star *
            </div>
            <div>
                <ul>
                    {sentenceBlankList.map((sentenceBlank, index) => (
                        <li key={index}>
                            {index % 2 === 0 ? 'A: ' : 'B: '}
                            <TextField
                                variant="outlined"
                                style={{ width: '80%' }}
                                value={sentenceBlankList[index] || ''}
                                onChange={(event) => handleSentenceBlankInputChange(index, event)}
                                onBlur={() => handleSentenceBlankInputValidation(index)}
                                error={Boolean(sentenceBlankErrorList[index])} // Set error prop based on error presence
                                helperText={sentenceBlankErrorList[index]} // Display error message
                            />
                        </li>
                    ))}
                </ul>
            </div>
        </Stack>
    </>);
};

export default DialogPlanLlmPromptConversationReadWithBlankSpeakGenerated;
